import {
  getFilterDocument,
  useSmartListById,
  useUpdateSmartList,
} from 'features/smart-lists';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useToggle } from 'shared/hooks/use-toggle';
import { FilterDocument } from 'shared/types/filter-document';

import { PageTopBar } from './components/page-top-bar';
import { SmartListTasksOverview } from './components/tasks-overview';
import * as Styled from './smart-list-detail.style';

export const SmartListDetail: React.FC = () => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const smartList = useSmartListById(id);
  const { submit: updateSmartList } = useUpdateSmartList();
  const [showCompleted, toggleShowCompleted] = useToggle();
  const [updatedFilterDocument, setUpdatedFilterDocument] = useState<
    FilterDocument | undefined
  >();

  const defaultFilterDocument = useMemo(
    () => getFilterDocument(smartList),
    [smartList],
  );

  const activeFilterDocument = useMemo(() => {
    if (!smartList) {
      return undefined;
    }
    return updatedFilterDocument ?? defaultFilterDocument;
  }, [defaultFilterDocument, smartList, updatedFilterDocument]);

  const resetUpdatedFilterDocument = () => setUpdatedFilterDocument(undefined);

  const onUpdateSmartList = async () => {
    if (!smartList || !activeFilterDocument) {
      return;
    }

    await updateSmartList({
      id: smartList.id,
      filterDocument: activeFilterDocument,
    });
    resetUpdatedFilterDocument();
  };

  useEffect(() => {
    // reset filter changes when moving to other smartList without saving first
    setUpdatedFilterDocument(undefined);
  }, [id]);

  if (!smartList || !activeFilterDocument) {
    return null;
  }

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <PageTopBar
            showCompleted={showCompleted}
            onToggleShowCompleted={toggleShowCompleted}
            filterDocument={activeFilterDocument}
            defaultFilterDocument={defaultFilterDocument}
            onUpdateFilterDocument={setUpdatedFilterDocument}
            onResetFilter={resetUpdatedFilterDocument}
            onUpdateSmartList={onUpdateSmartList}
          />
          <Styled.Content ref={scrollContainerRef}>
            <SmartListTasksOverview
              scrollElement={scrollContainerRef}
              smartList={smartList}
              showCompleted={showCompleted}
              filterDocument={activeFilterDocument}
              onReset={resetUpdatedFilterDocument}
            />
          </Styled.Content>
        </Styled.MainContent>
      </Styled.Main>
    </Styled.Container>
  );
};
